import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import { Layers, List as ListIcon, Search } from 'lucide-react';
import { InfoButton } from '../../components/InfoModal';

const TYPE_LABELS = {
  ENGINEER: 'Engineer',
  INSTALLER: 'Installer',
  DESIGNER: 'Designer',
};

function ExpertsTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-card shadow-sm">
      <table className="min-w-full text-sm divide-y divide-gray-200">
        <thead className="bg-navy-900 text-left text-xs uppercase tracking-wide text-white/80">
          <tr>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Group</th>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Company</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((ex) => (
            <tr key={ex.id} className="hover:bg-slate-50">
              <td className="px-4 py-3">
                <Link to={`/experts/${ex.id}/edit`} className="font-medium text-navy-900 hover:underline">
                  {ex.name}
                </Link>
              </td>
              <td className="px-4 py-3 text-gray-600">{TYPE_LABELS[ex.type]}</td>
              <td className="px-4 py-3 text-gray-500">{ex.expert_group || '—'}</td>
              <td className="px-4 py-3 text-gray-600">{ex.email || '—'}</td>
              <td className="px-4 py-3 text-gray-600">{ex.company_name || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EngineersTab() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [groupMode, setGroupMode] = useState('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['experts', { search, typeFilter }],
    queryFn: async () => {
      const res = await api.get('/experts', {
        params: { search: search || undefined, type: typeFilter || undefined },
      });
      return res.data?.data ?? [];
    },
    enabled: groupMode === 'all',
  });

  const { data: groupedData, isLoading: groupedLoading } = useQuery({
    queryKey: ['experts', 'grouped-by-ss'],
    queryFn: async () => (await api.get('/experts/grouped-by-ss')).data?.data ?? { bySs: {}, ssList: [] },
    enabled: groupMode === 'bySs',
  });

  const experts = Array.isArray(data) ? data : [];
  const bySs = groupedData?.bySs ?? {};

  return (
    <div>
      <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800">
        <strong>Engineers</strong> = for Compliance Schedule amendments (PS1, PS3, PS4, CoA). Separate from
        council-registered IQPs. Linked to amendment workflow by SS / expert group (Fire / Mech / Elec / Plumbing).
        <InfoButton title="Engineer vs IQP">
          <p>
            IQP = Form 12A inspection. Engineer = design / compliance amendment. Same company is fine; keep the roles
            separate.
          </p>
        </InfoButton>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1 bg-gray-50">
          <button
            type="button"
            onClick={() => setGroupMode('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
              groupMode === 'all' ? 'bg-white shadow text-primary-700' : 'text-gray-600'
            }`}
          >
            <ListIcon className="w-4 h-4" />
            All
          </button>
          <button
            type="button"
            onClick={() => setGroupMode('bySs')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
              groupMode === 'bySs' ? 'bg-white shadow text-primary-700' : 'text-gray-600'
            }`}
          >
            <Layers className="w-4 h-4" />
            By SS
          </button>
        </div>
        <p className="text-xs text-gray-500">Use <strong>Add engineer</strong> in the page header.</p>
      </div>

      {groupMode === 'all' && (
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, company…"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && <p className="text-red-600 mb-4">{error.message}</p>}

      {groupMode === 'bySs' ? (
        groupedLoading ? (
          <p className="text-center py-8 text-gray-500">Loading…</p>
        ) : Object.keys(bySs).length === 0 ? (
          <p className="text-center py-8 text-gray-500 text-sm">No engineers with SS codes yet.</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(bySs).map(([ssCode, list]) => (
              <div key={ssCode} className="bg-card rounded-lg border overflow-hidden">
                <div className="px-4 py-3 bg-navy-900 border-b text-white">
                  <h3 className="font-semibold">{ssCode}</h3>
                  <p className="text-sm text-white/70">{list.length} expert(s)</p>
                </div>
                <ExpertsTable rows={list} />
              </div>
            ))}
          </div>
        )
      ) : isLoading ? (
        <p className="text-center py-8 text-gray-500">Loading…</p>
      ) : experts.length === 0 ? (
        <p className="text-center py-8 text-gray-500 text-sm">No engineers yet.</p>
      ) : (
        <ExpertsTable rows={experts} />
      )}
    </div>
  );
}
